import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Building2, FileText, CheckCircle, Clock, AlertTriangle } from "lucide-react";
import { RecentCompletedTasks } from "@/components/admin/RecentCompletedTasks";
import { AllStatesProgressPanel } from "@/components/admin/AllStatesProgressPanel";

const US_STATES = [
  { code: 'AL', name: 'Alabama', estimatedHospitals: 88 },
  { code: 'AK', name: 'Alaska', estimatedHospitals: 25 }, // Based on research: 25 identified
  { code: 'AZ', name: 'Arizona', estimatedHospitals: 77 },
  { code: 'AR', name: 'Arkansas', estimatedHospitals: 75 },
  { code: 'CA', name: 'California', estimatedHospitals: 341 },
  { code: 'CO', name: 'Colorado', estimatedHospitals: 69 },
  { code: 'CT', name: 'Connecticut', estimatedHospitals: 35 }, // Based on research: 32 facilities found
  { code: 'DE', name: 'Delaware', estimatedHospitals: 7 },
  { code: 'FL', name: 'Florida', estimatedHospitals: 213 },
  { code: 'GA', name: 'Georgia', estimatedHospitals: 151 },
  { code: 'HI', name: 'Hawaii', estimatedHospitals: 25 },
  { code: 'ID', name: 'Idaho', estimatedHospitals: 42 },
  { code: 'IL', name: 'Illinois', estimatedHospitals: 184 },
  { code: 'IN', name: 'Indiana', estimatedHospitals: 124 },
  { code: 'IA', name: 'Iowa', estimatedHospitals: 117 },
  { code: 'KS', name: 'Kansas', estimatedHospitals: 125 },
  { code: 'KY', name: 'Kentucky', estimatedHospitals: 96 }, // Research identified 20+ major systems
  { code: 'LA', name: 'Louisiana', estimatedHospitals: 123 },
  { code: 'ME', name: 'Maine', estimatedHospitals: 36 },
  { code: 'MD', name: 'Maryland', estimatedHospitals: 46 },
  { code: 'MA', name: 'Massachusetts', estimatedHospitals: 62 },
  { code: 'MI', name: 'Michigan', estimatedHospitals: 134 },
  { code: 'MN', name: 'Minnesota', estimatedHospitals: 133 },
  { code: 'MS', name: 'Mississippi', estimatedHospitals: 95 },
  { code: 'MO', name: 'Missouri', estimatedHospitals: 125 },
  { code: 'MT', name: 'Montana', estimatedHospitals: 53 },
  { code: 'NE', name: 'Nebraska', estimatedHospitals: 83 },
  { code: 'NV', name: 'Nevada', estimatedHospitals: 36 },
  { code: 'NH', name: 'New Hampshire', estimatedHospitals: 26 },
  { code: 'NJ', name: 'New Jersey', estimatedHospitals: 71 },
  { code: 'NM', name: 'New Mexico', estimatedHospitals: 37 },
  { code: 'NY', name: 'New York', estimatedHospitals: 185 },
  { code: 'NC', name: 'North Carolina', estimatedHospitals: 112 },
  { code: 'ND', name: 'North Dakota', estimatedHospitals: 45 },
  { code: 'OH', name: 'Ohio', estimatedHospitals: 170 },
  { code: 'OK', name: 'Oklahoma', estimatedHospitals: 109 },
  { code: 'OR', name: 'Oregon', estimatedHospitals: 59 },
  { code: 'PA', name: 'Pennsylvania', estimatedHospitals: 169 },
  { code: 'RI', name: 'Rhode Island', estimatedHospitals: 11 },
  { code: 'SC', name: 'South Carolina', estimatedHospitals: 62 },
  { code: 'SD', name: 'South Dakota', estimatedHospitals: 52 },
  { code: 'TN', name: 'Tennessee', estimatedHospitals: 116 },
  { code: 'TX', name: 'Texas', estimatedHospitals: 450 }, // Large state with many hospitals
  { code: 'UT', name: 'Utah', estimatedHospitals: 47 },
  { code: 'VT', name: 'Vermont', estimatedHospitals: 14 },
  { code: 'VA', name: 'Virginia', estimatedHospitals: 88 },
  { code: 'WA', name: 'Washington', estimatedHospitals: 87 },
  { code: 'WV', name: 'West Virginia', estimatedHospitals: 54 },
  { code: 'WI', name: 'Wisconsin', estimatedHospitals: 125 },
  { code: 'WY', name: 'Wyoming', estimatedHospitals: 25 }
];

interface StateResearchSession {
  id: string;
  state_code: string;
  state_name: string;
  research_date: string;
  session_status: string;
  total_systems_identified: number;
  total_facilities_found: number;
  high_priority_systems: number;
  medium_priority_systems: number;
  low_priority_systems: number;
  files_ready_for_import: number;
  executive_summary: string;
  compliance_notes: string;
  manual_intervention_required: boolean;
  next_steps: string;
  created_at: string;
  updated_at: string;
}

interface HospitalSystemResearch {
  id: string;
  system_name: string;
  geographic_region: string;
  import_priority: string;
  system_size: number;
  cms_compliance_status: string;
  transparency_portal_url: string;
  system_status: string;
  agent_assigned: string;
  manual_intervention_needed: boolean;
  intervention_reason: string;
  facilities_count: number;
  files_accessible_count: number;
  total_estimated_records: number;
  research_notes: string;
  created_at: string;
  updated_at: string;
}

const StateIngestionDashboard = () => {
  const { stateCode } = useParams<{ stateCode: string }>();
  const navigate = useNavigate();
  const [selectedState, setSelectedState] = useState(stateCode?.toUpperCase() || 'CA');

  const currentState = US_STATES.find(s => s.code === selectedState);

  useEffect(() => {
    if (stateCode && stateCode.toUpperCase() !== selectedState) {
      setSelectedState(stateCode.toUpperCase());
    }
  }, [stateCode, selectedState]);

  // Fetch state research session data
  const { data: stateSession, isLoading: sessionLoading } = useQuery({
    queryKey: ['state-research-session', selectedState],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('state_research_sessions')
        .select('*')
        .eq('state_code', selectedState)
        .order('research_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as StateResearchSession | null;
    }
  });

  // Fetch hospital systems research data for the state
  const { data: hospitalSystems, isLoading: systemsLoading } = useQuery({
    queryKey: ['hospital-systems-research', selectedState, stateSession?.id],
    queryFn: async () => {
      if (!stateSession?.id) return [];

      const { data, error } = await supabase
        .from('hospital_systems_research')
        .select('*')
        .eq('session_id', stateSession.id)
        .order('import_priority', { ascending: true });

      if (error) throw error;
      return data as HospitalSystemResearch[];
    },
    enabled: !!stateSession?.id
  });

  // Fetch actual hospital completion data for the state using import_status (much faster)
  const { data: hospitalStats, isLoading: statsLoading } = useQuery({
    queryKey: ['hospital-completion-stats', selectedState],
    queryFn: async () => {
      // Use import_status to determine completion - much faster than joins
      const { data: statusCounts, error } = await supabase
        .from('hospitals')
        .select('import_status')
        .eq('state', selectedState);

      if (error) throw error;

      const total_hospitals = statusCounts?.length || 0;

      // Count hospitals with successfully imported data
      const completedStatuses = ['completed', 'imported', 'importing'];
      const hospitals_with_data = statusCounts?.filter(h =>
        completedStatuses.includes(h.import_status)
      ).length || 0;

      const completion_percentage = total_hospitals > 0 ?
        Math.round((hospitals_with_data / total_hospitals) * 100) : 0;

      return {
        total_hospitals,
        hospitals_with_data,
        completion_percentage
      };
    }
  });

  // Calculate progress metrics
  const estimatedHospitals = currentState?.estimatedHospitals || 0;
  const identifiedFacilities = stateSession?.total_facilities_found || 0;
  const completedSystems = hospitalSystems?.filter(s => s.system_status === 'completed').length || 0;
  const totalSystems = stateSession?.total_systems_identified || 0;

  // Use real database stats for actual hospitals
  const actualHospitalsInDB = hospitalStats?.total_hospitals || 0;
  const hospitalsWithData = hospitalStats?.hospitals_with_data || 0;
  const actualCompletionPercentage = hospitalStats?.completion_percentage || 0;

  // Research vs reality comparison
  const progressPercentage = estimatedHospitals > 0 ? Math.round((actualHospitalsInDB / estimatedHospitals) * 100) : 0;
  const systemsProgressPercentage = totalSystems > 0 ? Math.round((completedSystems / totalSystems) * 100) : 0;

  const handleStateChange = (newStateCode: string) => {
    setSelectedState(newStateCode);
    navigate(`/ingestion-dashboard/state/${newStateCode.toLowerCase()}`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'pending':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'destructive';
      case 'MEDIUM':
        return 'default';
      case 'LOW':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <Helmet>
        <title>State Ingestion Dashboard</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-3xl font-bold">State Ingestion Dashboard</h1>
              <p className="text-muted-foreground">
                Track hospital data ingestion progress by state
              </p>
            </div>
          </div>

          <div className="w-64">
            <Select value={selectedState} onValueChange={handleStateChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map(state => (
                  <SelectItem key={state.code} value={state.code}>
                    {state.name} ({state.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* All States Progress Overview */}
        <AllStatesProgressPanel />

        {/* State Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Estimated Total</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{estimatedHospitals.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                CMS estimated hospitals
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Database</CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{actualHospitalsInDB.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {progressPercentage}% of estimated found
              </p>
              <Progress value={Math.min(progressPercentage, 100)} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">With Pricing Data</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{hospitalsWithData.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {actualCompletionPercentage}% completion rate
              </p>
              <Progress value={actualCompletionPercentage} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Systems Identified</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalSystems}</div>
              <p className="text-xs text-muted-foreground">
                Hospital systems researched
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Systems Complete</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{completedSystems}</div>
              <p className="text-xs text-muted-foreground">
                {systemsProgressPercentage}% completion rate
              </p>
              <Progress value={systemsProgressPercentage} className="mt-2" />
            </CardContent>
          </Card>
        </div>

        {/* Overall Statistics Summary */}
        {!statsLoading && hospitalStats && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {currentState?.name} Ingestion Summary
                <Badge variant={actualCompletionPercentage >= 80 ? 'default' : actualCompletionPercentage >= 50 ? 'secondary' : 'destructive'}>
                  {actualCompletionPercentage}% Complete
                </Badge>
              </CardTitle>
              <CardDescription>
                Real-time hospital data ingestion progress for {currentState?.name}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h4 className="font-medium">Discovery Progress</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Estimated Hospitals:</span>
                      <span className="font-mono">{estimatedHospitals}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Found in Database:</span>
                      <span className="font-mono">{actualHospitalsInDB}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Discovery Rate:</span>
                      <span className="font-mono">{progressPercentage}%</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Data Ingestion</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Hospitals in DB:</span>
                      <span className="font-mono">{actualHospitalsInDB}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>With Pricing Data:</span>
                      <span className="font-mono">{hospitalsWithData}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Completion Rate:</span>
                      <span className="font-mono">{actualCompletionPercentage}%</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Research Status</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Systems Identified:</span>
                      <span className="font-mono">{totalSystems}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Systems Complete:</span>
                      <span className="font-mono">{completedSystems}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Research Progress:</span>
                      <span className="font-mono">{systemsProgressPercentage}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Overall Status:</span>
                  {actualCompletionPercentage >= 80 ? (
                    <span className="text-green-600">✅ Excellent Progress</span>
                  ) : actualCompletionPercentage >= 50 ? (
                    <span className="text-yellow-600">🟡 Good Progress</span>
                  ) : actualCompletionPercentage >= 25 ? (
                    <span className="text-orange-600">🟠 In Progress</span>
                  ) : (
                    <span className="text-red-600">🔴 Needs Attention</span>
                  )}
                  <span className="ml-auto text-muted-foreground">
                    {hospitalsWithData} of {estimatedHospitals} estimated hospitals ingested
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Research Session Summary */}
        {stateSession && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Latest Research Session
                <Badge variant={stateSession.session_status === 'completed' ? 'default' : 'secondary'}>
                  {stateSession.session_status}
                </Badge>
              </CardTitle>
              <CardDescription>
                Research conducted on {new Date(stateSession.research_date).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {stateSession.executive_summary && (
                <div>
                  <h4 className="font-medium mb-2">Executive Summary</h4>
                  <p className="text-sm text-muted-foreground">{stateSession.executive_summary}</p>
                </div>
              )}

              {stateSession.compliance_notes && (
                <div>
                  <h4 className="font-medium mb-2">Compliance Notes</h4>
                  <p className="text-sm text-muted-foreground">{stateSession.compliance_notes}</p>
                </div>
              )}

              {stateSession.next_steps && (
                <div>
                  <h4 className="font-medium mb-2">Next Steps</h4>
                  <p className="text-sm text-muted-foreground">{stateSession.next_steps}</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600">{stateSession.high_priority_systems}</div>
                  <div className="text-xs text-muted-foreground">High Priority</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-600">{stateSession.medium_priority_systems}</div>
                  <div className="text-xs text-muted-foreground">Medium Priority</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">{stateSession.low_priority_systems}</div>
                  <div className="text-xs text-muted-foreground">Low Priority</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hospital Systems Table */}
        <Card>
          <CardHeader>
            <CardTitle>Hospital Systems Research Results</CardTitle>
            <CardDescription>
              Detailed breakdown of hospital systems found in {currentState?.name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {systemsLoading ? (
              <div className="text-center py-8">Loading hospital systems...</div>
            ) : !hospitalSystems || hospitalSystems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No research data available for {currentState?.name} yet.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>System Name</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Facilities</TableHead>
                    <TableHead>Files Ready</TableHead>
                    <TableHead>Est. Records</TableHead>
                    <TableHead>Compliance</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hospitalSystems.map((system) => (
                    <TableRow key={system.id}>
                      <TableCell className="font-medium">
                        <div>
                          {system.system_name}
                          {system.geographic_region && (
                            <div className="text-xs text-muted-foreground">
                              {system.geographic_region}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityColor(system.import_priority)}>
                          {system.import_priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(system.system_status)}
                          <span className="capitalize">{system.system_status}</span>
                        </div>
                      </TableCell>
                      <TableCell>{system.facilities_count}</TableCell>
                      <TableCell>{system.files_accessible_count}</TableCell>
                      <TableCell>{system.total_estimated_records?.toLocaleString() || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={
                          system.cms_compliance_status === 'compliant' ? 'default' :
                          system.cms_compliance_status === 'non_compliant' ? 'destructive' :
                          'secondary'
                        }>
                          {system.cms_compliance_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {system.agent_assigned || 'Unassigned'}
                      </TableCell>
                      <TableCell>
                        {system.research_notes ? (
                          <div className="max-w-xs">
                            <p className="text-xs truncate" title={system.research_notes}>
                              {system.research_notes}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recently Completed Tasks */}
        <RecentCompletedTasks limit={5} />
      </div>
    </div>
  );
};

export default StateIngestionDashboard;